import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Calendar, Clock, AlertTriangle, CheckCircle2, Plus, RefreshCw, Receipt } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_PILL = {
  pending:    'bg-amber-100 text-amber-700',
  paid:       'bg-emerald-100 text-emerald-700',
  cancelled:  'bg-gray-100 text-gray-500',
};

const EXPIRY_PILL = {
  active:         'bg-gray-100 text-gray-600',
  expiring_soon:  'bg-amber-100 text-amber-700',
  expired:        'bg-red-100 text-red-700',
  completed:      'bg-emerald-50 text-emerald-700',
};

const AdminQuotes = () => {
  const { token } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [filter, setFilter] = useState('all'); // all | expired | expiring_soon | paid_in_store
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [extending, setExtending] = useState(null);

  const fetchQuotes = async (expiredOnly = false) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/quotes/admin/all${expiredOnly ? '?expired_only=true' : ''}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setQuotes(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load quotes');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (token) fetchQuotes(filter === 'expired'); }, [token, filter]);

  const handleExtend = async (quoteId, days = 30) => {
    setExtending(quoteId);
    try {
      await axios.post(
        `${API_URL}/api/quotes/admin/${quoteId}/extend`,
        { days, reason: 'Staff extension from admin panel' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchQuotes(filter === 'expired');
    } catch (e) { setError(e.response?.data?.detail || 'Failed to extend'); }
    finally { setExtending(null); }
  };

  const filtered = quotes.filter(q => {
    if (filter === 'all') return true;
    if (filter === 'expired') return q.expiry_state === 'expired';
    if (filter === 'expiring_soon') return q.expiry_state === 'expiring_soon';
    if (filter === 'paid_in_store') return q.paid_in_store;
    return true;
  });

  return (
    <AdminLayout>
      <div className="px-6 py-6 w-full min-w-0" data-testid="admin-quotes-page">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-[#1B3A5C]">All Quotes</h1>
            <p className="text-sm text-gray-500">Track expirations, extend deadlines, and review in-store payments.</p>
          </div>
          <div className="flex items-center gap-2">
            <a href="/admin/in-store-quote" className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#F5A623] text-[#1B3A5C] font-bold text-sm rounded-full hover:bg-[#D88F1B]">
              <Plus className="w-4 h-4" /> New In-Store Quote
            </a>
            <button onClick={() => fetchQuotes(filter === 'expired')} className="p-2 border border-gray-200 rounded-full hover:bg-gray-50">
              <RefreshCw className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-5 overflow-x-auto">
          {[
            { k: 'all', label: 'All' },
            { k: 'expiring_soon', label: 'Expiring Soon' },
            { k: 'expired', label: 'Expired' },
            { k: 'paid_in_store', label: 'Paid In-Store' },
          ].map(({ k, label }) => (
            <button key={k} onClick={() => setFilter(k)} data-testid={`quotes-filter-${k}`}
              className={`px-4 py-1.5 rounded-full text-xs font-bold whitespace-nowrap transition ${
                filter === k ? 'bg-[#1B3A5C] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}>
              {label}
            </button>
          ))}
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm mb-4 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> {error}
          </div>
        )}

        {loading ? (
          <div className="text-gray-400 text-sm py-12 text-center">Loading quotes…</div>
        ) : filtered.length === 0 ? (
          <div className="text-gray-400 text-sm py-12 text-center bg-white border border-dashed border-gray-200 rounded-xl">No quotes match this filter.</div>
        ) : (
          <div className="space-y-3">
            {filtered.map(q => (
              <div key={q.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center justify-between gap-4 flex-wrap" data-testid={`admin-quote-${q.quote_number}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                    <span className="font-bold text-[#1B3A5C]">{q.quote_number}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${STATUS_PILL[q.status] || 'bg-gray-100 text-gray-500'}`}>
                      {q.status}
                    </span>
                    {q.expiry_state && q.expiry_state !== 'completed' && (
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${EXPIRY_PILL[q.expiry_state]}`}>
                        {q.expiry_state === 'expired'
                          ? 'Expired'
                          : q.expiry_state === 'expiring_soon'
                          ? `Expires in ${q.days_until_expiry}d`
                          : `${q.days_until_expiry}d left`}
                      </span>
                    )}
                    {q.paid_in_store && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-[#1B3A5C] text-white inline-flex items-center gap-1">
                        <Receipt className="w-3 h-3" />
                        In-store {q.in_store_tender ? `· ${q.in_store_tender}` : ''}
                      </span>
                    )}
                    {q.extension_count > 0 && (
                      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-700">
                        Ext ×{q.extension_count}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-700 truncate">
                    {q.customer_name} <span className="text-gray-400">·</span>{' '}
                    <a href={`mailto:${q.customer_email}`} className="text-[#4A90A4] hover:underline">{q.customer_email}</a>
                  </p>
                  <div className="flex items-center gap-4 mt-1.5 text-xs text-gray-500">
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {q.created_at ? new Date(q.created_at).toLocaleDateString() : '-'}</span>
                    {q.expires_at && (
                      <span className="inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Expires {new Date(q.expires_at).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-400">Total</p>
                  <p className="text-lg font-bold text-[#1B3A5C]">${q.total?.toFixed(2)}</p>
                  <p className="text-[11px] text-[#F5A623] font-semibold">${q.deposit_amount?.toFixed(2)} deposit</p>
                </div>
                <div className="flex flex-col gap-2 min-w-[140px]">
                  {q.status === 'pending' && (q.expiry_state === 'expired' || q.expiry_state === 'expiring_soon') && (
                    <button
                      onClick={() => handleExtend(q.id, 30)}
                      disabled={extending === q.id}
                      data-testid={`extend-quote-${q.quote_number}`}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-1.5 bg-[#4A90A4] text-white font-bold text-xs rounded-full hover:bg-[#3a7183] disabled:opacity-50"
                    >
                      <Clock className="w-3.5 h-3.5" />
                      {extending === q.id ? 'Extending…' : 'Extend +30d'}
                    </button>
                  )}
                  {q.status === 'paid' && (
                    <span className="inline-flex items-center justify-center gap-1 text-xs text-emerald-600 font-semibold">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Paid
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminQuotes;
